import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loadRuntimeConfig, type RuntimeConfig } from './config';

interface TokenSet {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt: number;
}

interface AuthContextValue {
  config: RuntimeConfig | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login(): Promise<void>;
  completeCallback(url: string): Promise<void>;
  signOut(): Promise<void>;
}

const TOKEN_KEY = 'human-studio-admin.tokens';
const PKCE_KEY = 'human-studio-admin.pkce';
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [tokens, setTokens] = useState<TokenSet | null>(() => readTokens());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadRuntimeConfig()
      .then(setConfig)
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async () => {
    const currentConfig = config ?? (await loadRuntimeConfig());
    if (!currentConfig.cognitoDomain || !currentConfig.cognitoClientId) {
      throw new Error('Cognito configuration is missing');
    }
    const verifier = randomString(64);
    const challenge = await sha256Base64Url(verifier);
    sessionStorage.setItem(PKCE_KEY, verifier);
    const params = new URLSearchParams({
      client_id: currentConfig.cognitoClientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: currentConfig.cognitoRedirectUri,
      code_challenge_method: 'S256',
      code_challenge: challenge
    });
    window.location.href = `${trimSlash(currentConfig.cognitoDomain)}/oauth2/authorize?${params}`;
  }, [config]);

  const completeCallback = useCallback(
    async (url: string) => {
      const currentConfig = config ?? (await loadRuntimeConfig());
      const parsed = new URL(url);
      const code = parsed.searchParams.get('code');
      const verifier = sessionStorage.getItem(PKCE_KEY);
      if (!code || !verifier) {
        throw new Error('Missing sign-in callback code');
      }
      const response = await fetch(`${trimSlash(currentConfig.cognitoDomain)}/oauth2/token`, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: currentConfig.cognitoClientId,
          code,
          redirect_uri: currentConfig.cognitoRedirectUri,
          code_verifier: verifier
        })
      });
      if (!response.ok) {
        throw new Error('Could not complete sign in');
      }
      const payload = (await response.json()) as {
        access_token: string;
        id_token?: string;
        refresh_token?: string;
        expires_in: number;
      };
      const nextTokens: TokenSet = {
        accessToken: payload.access_token,
        idToken: payload.id_token,
        refreshToken: payload.refresh_token,
        expiresAt: Date.now() + payload.expires_in * 1000
      };
      sessionStorage.removeItem(PKCE_KEY);
      localStorage.setItem(TOKEN_KEY, JSON.stringify(nextTokens));
      setTokens(nextTokens);
    },
    [config]
  );

  const signOut = useCallback(async () => {
    localStorage.removeItem(TOKEN_KEY);
    setTokens(null);
    const currentConfig = config ?? (await loadRuntimeConfig());
    if (currentConfig.cognitoDomain && currentConfig.cognitoClientId) {
      const params = new URLSearchParams({
        client_id: currentConfig.cognitoClientId,
        logout_uri: window.location.origin
      });
      window.location.href = `${trimSlash(currentConfig.cognitoDomain)}/logout?${params}`;
    }
  }, [config]);

  const value = useMemo<AuthContextValue>(() => {
    const validTokens = tokens && tokens.expiresAt > Date.now() + 30_000 ? tokens : null;
    return {
      config,
      token: validTokens?.idToken ?? validTokens?.accessToken ?? null,
      isAuthenticated: Boolean(validTokens),
      isLoading,
      login,
      completeCallback,
      signOut
    };
  }, [completeCallback, config, isLoading, login, signOut, tokens]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return value;
}

function readTokens(): TokenSet | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) {
      return null;
    }
    const tokens = JSON.parse(raw) as TokenSet;
    return tokens.expiresAt > Date.now() + 30_000 ? tokens : null;
  } catch {
    return null;
  }
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => (byte % 36).toString(36)).join('');
}

async function sha256Base64Url(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function trimSlash(value: string): string {
  return value.replace(/\/+$/, '');
}
